<template>
  <div class="eval-admin">

    <!-- ============ 登录 ============ -->
    <div class="center-wrap" v-if="!token">
      <el-card class="login-card" shadow="always">
        <h1 class="title">学生评价老师 · 统计</h1>
        <p class="sub">教务处后台</p>
        <el-form @submit.native.prevent>
          <el-form-item label="账号">
            <el-input v-model.trim="form.admin_name" placeholder="后台账号"></el-input>
          </el-form-item>
          <el-form-item label="密码">
            <el-input v-model="form.password" type="password" show-password
                      placeholder="后台密码" @keyup.enter.native="doLogin"></el-input>
          </el-form-item>
          <el-button type="primary" class="block-btn" :loading="loading" @click="doLogin">登 录</el-button>
        </el-form>

        <p class="tip">使用原有后台账号（think_admin 表）登录，与学生端互不影响。</p>
      </el-card>
    </div>

    <!-- ============ 统计 ============ -->
    <div v-else class="admin-main">
      <header class="topbar">
        <div class="topbar-inner">
          <strong>学生评价老师 · 统计</strong>
          <div class="right">
            <el-select v-model="batchId" size="small" @change="load" style="width:200px">
              <el-option v-for="b in batches" :key="b.id" :label="b.name" :value="b.id"></el-option>
            </el-select>
            <el-dropdown trigger="click" @command="exportCsv" style="vertical-align:middle">
              <el-button size="small" icon="el-icon-download">
                导出表格<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="eval">教学评价表（每月·按学生+教师）</el-dropdown-item>
                <el-dropdown-item command="ask">提问次数表（每周·按周展开）</el-dropdown-item>
                <el-dropdown-item command="talk">导师谈话表（每周·按学生+周）</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button size="small" icon="el-icon-date" @click="openBatch">批次管理</el-button>
            <el-button size="small" icon="el-icon-time" @click="openTime">时间设置</el-button>
            <el-button size="small" icon="el-icon-key" @click="openPwd">密码重置</el-button>
            <el-link :underline="false" @click="logout">退出</el-link>
          </div>
        </div>
      </header>

      <main class="body" v-if="overview">
        <!-- 概览 -->
        <div class="stat-row">
          <div class="stat">
            <span class="num">{{ overview.student_submitted }}<i>/{{ overview.student_total }}</i></span>
            <span class="label">已提交学生</span>
          </div>
          <div class="stat">
            <span class="num">{{ overview.submit_rate }}<i>%</i></span>
            <span class="label">提交率</span>
          </div>
          <div class="stat">
            <span class="num">{{ overview.avg_score }}<i>分</i></span>
            <span class="label">全校平均分（百分制）</span>
          </div>
          <div class="stat">
            <span class="num">{{ overview.teacher_total }}</span>
            <span class="label">被评教师数</span>
          </div>
          <div class="stat">
            <span class="num">{{ overview.ask_total }}</span>
            <span class="label">提问次数合计</span>
          </div>
          <div class="stat">
            <span class="num">{{ overview.talk_total || 0 }}</span>
            <span class="label">导师谈话合计（本周 {{ overview.talk_week_students || 0 }} 人上报）</span>
          </div>
        </div>

        <!-- 各指标平均分 -->
        <el-card class="panel" shadow="never">
          <div slot="header"><b>教学常规各项平均分（5 星制）</b></div>
          <div class="bar-row" v-for="it in items" :key="it.id">
            <span class="bar-name">{{ it.name }}</span>
            <el-progress class="bar-track" :percentage="it.avg_score / 5 * 100"
                         :show-text="false" :stroke-width="10"></el-progress>
            <span class="bar-val">{{ it.avg_score }}</span>
          </div>
          <p class="muted" v-if="items.length === 0">还没有评价数据</p>
        </el-card>

        <!-- 教师排行 -->
        <el-card class="panel" shadow="never">
          <div slot="header"><b>教师评价排行</b></div>
          <el-table :data="teachers" size="small" :default-sort="{ prop: 'avg_star', order: 'descending' }">
            <el-table-column type="index" label="名次" width="60"></el-table-column>
            <el-table-column prop="name" label="教师"></el-table-column>
            <el-table-column prop="student_count" label="评价人数" width="110"></el-table-column>
            <el-table-column prop="avg_star" label="平均星级" width="110" sortable></el-table-column>
            <el-table-column prop="avg_score" label="百分制" width="110" sortable></el-table-column>
            <el-table-column prop="ask_total" label="提问合计" width="110"></el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="openDetail(scope.row)">明细</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <!-- 班级进度 -->
        <el-card class="panel" shadow="never">
          <div slot="header"><b>班级提交进度</b></div>
          <el-table :data="classes" size="small">
            <el-table-column prop="class_name" label="班级">
              <template slot-scope="scope">{{ scope.row.class_name || '（未分班）' }}</template>
            </el-table-column>
            <el-table-column prop="total" label="应评人数" width="110"></el-table-column>
            <el-table-column prop="done" label="已提交" width="110"></el-table-column>
            <el-table-column label="完成率" width="220">
              <template slot-scope="scope">
                <el-progress :percentage="scope.row.total ? Math.round(scope.row.done / scope.row.total * 100) : 0"
                             :stroke-width="10"></el-progress>
              </template>
            </el-table-column>
            <el-table-column label="班级均分" width="110">
              <template slot-scope="scope">{{ scope.row.avg_star ? (scope.row.avg_star * 20).toFixed(1) : '-' }}</template>
            </el-table-column>
          </el-table>
        </el-card>

        <!-- 导师谈话（每周学生自报） -->
        <el-card class="panel" shadow="never">
          <div slot="header"><b>导师谈话汇总</b></div>
          <el-table :data="mentors" size="small">
            <el-table-column prop="mentor_name" label="导师"></el-table-column>
            <el-table-column prop="students" label="学生数" width="110"></el-table-column>
            <el-table-column prop="talk_total" label="谈话次数合计" width="130"></el-table-column>
            <el-table-column prop="weeks" label="上报周数" width="110"></el-table-column>
          </el-table>
          <p class="muted" v-if="mentors.length === 0">还没有学生填报导师谈话</p>
        </el-card>

        <p class="muted" style="padding: 4px 2px 30px">提示：学生意见仅显示文字，不显示学生姓名，保证评价匿名。</p>
      </main>

      <!-- 教师明细弹层 -->
      <el-dialog :title="(detail.teacher ? detail.teacher.name : '') + ' · 评价明细'"
                 :visible.sync="dialogVisible" width="640px" top="6vh">
        <div v-if="detail">
          <div class="bar-row" v-for="(it, i) in detail.items" :key="i">
            <span class="bar-name">{{ it.name }}</span>
            <el-progress class="bar-track" :percentage="it.avg_score / 5 * 100"
                         :show-text="false" :stroke-width="10"></el-progress>
            <span class="bar-val">{{ it.avg_score }}</span>
          </div>

          <h4>按班级</h4>
          <p class="muted" v-for="c in detail.classes" :key="c.class_name">
            {{ c.class_name }}：{{ c.student_count }} 人评价，平均 {{ c.avg_star }} 星
          </p>

          <h4>学生意见（{{ detail.comments.length }}）</h4>
          <p class="comment-item" v-for="(c, i) in detail.comments" :key="'c' + i">「{{ c.comment }}」</p>
          <p class="muted" v-if="detail.comments.length === 0">暂无文字意见</p>
        </div>
      </el-dialog>

      <!-- 批次管理弹层 -->
      <el-dialog title="评价批次管理" :visible.sync="batchVisible" width="680px" top="6vh">
        <el-form inline size="small" @submit.native.prevent>
          <el-form-item label="批次名称">
            <el-input v-model.trim="batchForm.name" placeholder="如：2026-2027学年第二学期" style="width:230px"></el-input>
          </el-form-item>
          <el-form-item label="起止日期">
            <el-date-picker v-model="batchForm.range" type="daterange" value-format="yyyy-MM-dd"
                            range-separator="至" start-placeholder="开始" end-placeholder="结束"
                            style="width:260px"></el-date-picker>
          </el-form-item>
          <el-button type="primary" size="small" :loading="batchSaving" @click="saveBatch">开启新批次</el-button>
        </el-form>

        <el-table :data="batchList" size="small">
          <el-table-column prop="name" label="批次"></el-table-column>
          <el-table-column prop="start_time" label="开始" width="110"></el-table-column>
          <el-table-column prop="end_time" label="结束" width="110"></el-table-column>
          <el-table-column label="状态" width="150">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.is_current" size="mini" type="success">当前批次</el-tag>
              <el-tag v-else size="mini" type="info">历史</el-tag>
              <el-tag v-if="scope.row.running" size="mini">进行中</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="record_total" label="评价条数" width="90"></el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="scope">
              <el-button v-if="!scope.row.is_current" type="text" size="small"
                         @click="activateBatch(scope.row)">设为当前</el-button>
            </template>
          </el-table-column>
        </el-table>

        <p class="muted" style="margin-top:10px">开启新批次会自动把旧批次关闭；学生在页面看到的、提交的评价都归属「当前批次」。评价指标调整仍可在 think_eval_item 表中增删改（前端自动跟随）。</p>
      </el-dialog>

      <!-- 密码重置弹层 -->
      <el-dialog title="学生密码重置" :visible.sync="pwdVisible" width="720px" top="6vh">
        <el-form inline size="small" @submit.native.prevent>
          <el-form-item>
            <el-input v-model.trim="pwdKeyword" placeholder="姓名 / 身份证号 / 学号，回车搜索"
                      clearable style="width:280px" @keyup.enter.native="searchStudents" @clear="searchStudents"></el-input>
          </el-form-item>
          <el-button size="small" icon="el-icon-search" @click="searchStudents">搜索</el-button>
          <el-button size="small" type="primary" plain @click="searchStudents('')">全部</el-button>
          <el-button size="small" type="danger" plain class="reset-all-btn"
                     @click="resetAllPwd">一键重置全部学生密码</el-button>
        </el-form>

        <el-table :data="students" size="small" max-height="380">
          <el-table-column prop="class_name" label="班级" width="120"></el-table-column>
          <el-table-column prop="name" label="姓名" width="110"></el-table-column>
          <el-table-column prop="account" label="账号（身份证号）" width="180"></el-table-column>
          <el-table-column label="状态" width="110">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.locked" size="mini" type="danger">已锁定</el-tag>
              <el-tag v-else size="mini" type="success">正常</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="130">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="resetOnePwd(scope.row)">重置密码</el-button>
            </template>
          </el-table-column>
        </el-table>

        <p class="muted" style="margin-top:10px">重置后密码 = 该学生身份证后 6 位，登录时会要求重新设置密码；同时清除密码错误锁定。小程序端密码与本端共用，重置后两端同步生效。</p>
      </el-dialog>

      <!-- 时间设置弹层 -->
      <el-dialog title="提交时间窗口设置" :visible.sync="timeVisible" width="640px" top="6vh">
        <el-alert type="info" :closable="false" style="margin-bottom:16px"
                  title="控制在每周 / 每月的哪个时间段允许学生提交。批次日期仍是最外层的总开关。"
                  description="截止日选 29/30/31 日时，当月没有那一天（如 2 月）会自动按当月最后一天处理。"></el-alert>

        <h4 class="time-group-title">每周提问次数上报</h4>
        <el-form inline size="small" label-width="72px" @submit.native.prevent>
          <el-form-item label="开放时间">
            <el-select v-model="timeForm.week_open_day" style="width:110px">
              <el-option v-for="(n, i) in weekDays" :key="'wo' + i" :label="'周' + n" :value="i + 1"></el-option>
            </el-select>
            <span class="time-sep">00:00 起可提交</span>
          </el-form-item>
          <el-form-item label="截止时间">
            <el-select v-model="timeForm.week_deadline_day" style="width:110px">
              <el-option v-for="(n, i) in weekDays" :key="'wd' + i" :label="'周' + n" :value="i + 1"></el-option>
            </el-select>
            <el-time-select v-model="timeForm.week_deadline_time" :picker-options="{ start: '00:00', step: '00:30', end: '23:30' }"
                            style="width:110px"></el-time-select>
            <span class="time-sep">后不可提交</span>
          </el-form-item>
        </el-form>

        <h4 class="time-group-title">每月教学评价</h4>
        <el-form inline size="small" label-width="72px" @submit.native.prevent>
          <el-form-item label="开放时间">
            <el-select v-model="timeForm.month_open_day" style="width:110px">
              <el-option v-for="i in 31" :key="'mo' + i" :label="i + '日'" :value="i"></el-option>
            </el-select>
            <span class="time-sep">00:00 起可提交</span>
          </el-form-item>
          <el-form-item label="截止时间">
            <el-select v-model="timeForm.month_deadline_day" style="width:130px">
              <el-option v-for="i in 31" :key="'md' + i" :label="i === 31 ? '31日（月末）' : i + '日'" :value="i"></el-option>
            </el-select>
            <el-time-select v-model="timeForm.month_deadline_time" :picker-options="{ start: '00:00', step: '00:30', end: '23:30' }"
                            style="width:110px"></el-time-select>
            <span class="time-sep">后不可提交</span>
          </el-form-item>
        </el-form>
        <p class="muted" style="margin:-4px 0 8px 72px">
          选「31 日（月末）」即按当月最后一天截止：10 月为 31 日、4 月为 30 日、2 月为 28/29 日，不用每月调整。
        </p>

        <p class="muted" v-if="timeInfo" v-html="timeInfo"></p>
        <div style="text-align:right;margin-top:14px">
          <el-button size="small" @click="timeVisible = false">取消</el-button>
          <el-button type="primary" size="small" :loading="timeSaving" @click="saveTime">保存设置</el-button>
        </div>
      </el-dialog>
    </div>

  </div>
</template>

<script>
import {
  evalAdminLogin, evalAdminStats, evalAdminDetail, evalAdminExportUrl,
  evalAdminBatchList, evalAdminBatchSave, evalAdminBatchActivate,
  evalAdminStudentList, evalAdminStudentReset, evalAdminStudentResetAll,
  evalAdminConfigGet, evalAdminConfigSave
} from '@/api/eval.js'

const TOKEN_KEY = 'eval_admin_token'

export default {
  name: 'DtEvalAdmin',
  data() {
    return {
      token: localStorage.getItem(TOKEN_KEY) || '',
      loading: false,
      form: { admin_name: '', password: '' },

      batchId: 0,
      batches: [],
      overview: null,
      teachers: [],
      classes: [],
      items: [],
      mentors: [],

      dialogVisible: false,
      // 给默认结构，避免弹层模板在首次渲染时读 undefined
      detail: { teacher: null, items: [], classes: [], comments: [] },

      // 批次管理
      batchVisible: false,
      batchSaving: false,
      batchForm: { name: '', range: null },
      batchList: [],

      // 密码重置
      pwdVisible: false,
      pwdKeyword: '',
      students: [],

      // 时间设置
      timeVisible: false,
      timeSaving: false,
      timeInfo: '',
      weekDays: ['一', '二', '三', '四', '五', '六', '日'],
      timeForm: {
        week_open_day: 1, week_deadline_day: 7, week_deadline_time: '23:30',
        month_open_day: 1, month_deadline_day: 31, month_deadline_time: '23:30'
      }
    }
  },
  mounted() {
    // 支持 ?token=xxx 直达
    const m = window.location.search.match(/[?&]token=([^&]+)/)
    if (m) {
      localStorage.setItem(TOKEN_KEY, decodeURIComponent(m[1]))
      this.token = localStorage.getItem(TOKEN_KEY)
    }
    if (this.token) {
      this.load()
    }
  },
  methods: {
    doLogin() {
      if (!this.form.admin_name || !this.form.password) {
        this.$message.warning('请输入账号和密码')
        return
      }
      this.loading = true
      evalAdminLogin(this.form).then((res) => {
        this.loading = false
        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }
        localStorage.setItem(TOKEN_KEY, res.data.token)
        this.token = res.data.token
        this.load()
      }).catch(() => {
        this.loading = false
        this.$message.error('网络异常，请稍后重试')
      })
    },

    load() {
      this.loading = true
      evalAdminStats({ batch_id: this.batchId }).then((res) => {
        this.loading = false
        if (res.code === 401) {
          this.logout()
          return
        }
        if (res.code !== 0) {
          this.$message.warning(res.msg)
          return
        }
        const d = res.data
        this.batches = d.batches
        if (!this.batchId) {
          this.batchId = d.batch.id
        }
        this.overview = d.overview
        this.teachers = d.teachers
        this.classes = d.classes
        this.items = d.items
        this.mentors = d.mentors || []
      }).catch(() => {
        this.loading = false
        this.$message.error('网络异常，请稍后重试')
      })
    },

    openDetail(teacher) {
      evalAdminDetail({
        teacher_info_id: teacher.teacher_info_id,
        batch_id: this.batchId
      }).then((res) => {
        if (res.code !== 0) {
          this.$message.warning(res.msg)
          return
        }
        this.detail = res.data
        this.dialogVisible = true
      })
    },

    // type: eval=教学评价表 | ask=提问次数表 | talk=导师谈话表
    exportCsv(type) {
      window.open(evalAdminExportUrl(type || 'eval', this.batchId), '_blank')
    },

    /* ---------------- 批次管理 ---------------- */

    openBatch() {
      this.batchVisible = true
      this.loadBatches()
    },

    loadBatches() {
      evalAdminBatchList().then((res) => {
        if (res.code !== 0) {
          this.$message.warning(res.msg)
          return
        }
        this.batchList = res.data.batches
      })
    },

    saveBatch() {
      const f = this.batchForm
      if (!f.name || !f.range || f.range.length !== 2) {
        this.$message.warning('请填写批次名称和起止日期')
        return
      }
      this.$confirm(`开启新批次「${f.name}」？旧批次将自动关闭，学生评价将计入新批次。`, '开启新批次', { type: 'warning' })
        .then(() => {
          this.batchSaving = true
          return evalAdminBatchSave({ name: f.name, start_time: f.range[0], end_time: f.range[1] })
        })
        .then((res) => {
          if (!res) return
          this.batchSaving = false
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }
          this.$message.success(res.msg)
          this.batchForm = { name: '', range: null }
          this.loadBatches()
          this.load()
        })
        .catch(() => { this.batchSaving = false })
    },

    activateBatch(row) {
      this.$confirm(`把「${row.name}」设为当前批次？学生评价将计入该批次。`, '切换批次', { type: 'warning' })
        .then(() => evalAdminBatchActivate({ id: row.id }))
        .then((res) => {
          if (!res || res.code !== 0) {
            if (res) this.$message.error(res.msg)
            return
          }
          this.$message.success(res.msg)
          this.loadBatches()
          this.load()
        })
        .catch(() => {})
    },

    /* ---------------- 密码重置 ---------------- */

    openPwd() {
      this.pwdVisible = true
      if (this.students.length === 0) {
        this.searchStudents()
      }
    },

    searchStudents(kw) {
      if (typeof kw === 'string') {
        this.pwdKeyword = kw
      }
      evalAdminStudentList({ keyword: this.pwdKeyword }).then((res) => {
        if (res.code !== 0) {
          this.$message.warning(res.msg)
          return
        }
        this.students = res.data.students
      })
    },

    resetOnePwd(row) {
      this.$confirm(`把 ${row.class_name} ${row.name} 的密码重置为身份证后 6 位？`, '重置密码', { type: 'warning' })
        .then(() => evalAdminStudentReset({ student_info_id: row.id }))
        .then((res) => {
          if (!res || res.code !== 0) {
            if (res) this.$message.error(res.msg)
            return
          }
          this.$message.success(`${row.name} 的密码已重置为 ${res.data.password}，登录后需重新设置`)
          this.searchStudents()
        })
        .catch(() => {})
    },

    resetAllPwd() {
      this.$prompt('此操作会把全部学生的密码重置为各自身份证后 6 位（含已修改过密码的学生），确定请输入：重置', '一键重置全部密码', {
        type: 'warning',
        inputPattern: /^重置$/,
        inputErrorMessage: '请输入「重置」以确认'
      }).then(() => {
        const loading = this.$loading({ lock: true, text: '正在重置，请稍候…' })
        evalAdminStudentResetAll().then((res) => {
          loading.close()
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }
          this.$message.success(res.msg)
          this.searchStudents()
        }).catch(() => {
          loading.close()
          this.$message.error('网络异常，请稍后重试')
        })
      }).catch(() => {})
    },

    /* ---------------- 时间设置 ---------------- */

    openTime() {
      this.timeVisible = true
      evalAdminConfigGet().then((res) => {
        if (res.code !== 0) {
          this.$message.warning(res.msg)
          return
        }
        this.timeForm = res.data.config
        const w = res.data.week_window
        const m = res.data.month_window
        this.timeInfo = '当前状态 —— 每周上报：' + this.winText(w)
          + '；本月评价：' + this.winText(m)
      })
    },

    winText(w) {
      const s = w.status === 'open' ? '<span style="color:#67c23a">开放中</span>'
        : w.status === 'pending' ? '<span style="color:#e6a23c">未开始</span>'
        : '<span style="color:#f56c6c">已截止</span>'
      return s + '（' + w.open_text + ' ~ ' + w.deadline_text + '）'
    },

    saveTime() {
      const f = this.timeForm
      if (f.week_open_day > f.week_deadline_day) {
        this.$message.warning('每周开放日不能晚于截止日')
        return
      }
      if (f.month_open_day > f.month_deadline_day) {
        this.$message.warning('每月开放日不能晚于截止日')
        return
      }
      this.timeSaving = true
      evalAdminConfigSave(f).then((res) => {
        this.timeSaving = false
        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }
        this.$message.success('时间窗口已更新，立即生效')
        this.openTime()
      }).catch(() => {
        this.timeSaving = false
        this.$message.error('网络异常，请稍后重试')
      })
    },

    logout() {
      localStorage.removeItem(TOKEN_KEY)
      this.token = ''
      this.form.password = ''
      this.overview = null
      this.teachers = []
      this.classes = []
      this.items = []
    }
  }
}
</script>

<style scoped lang="scss">
.eval-admin {
  min-height: 100vh;
  background: #f2f4f8;
  color: #1f2733;
  font-size: 14px;
}

.muted {
  color: #8a94a6;
  font-size: 13px;
}

.time-group-title {
  margin: 4px 0 10px;
  color: #37415a;
}

.time-sep {
  color: #8a94a6;
  font-size: 12px;
  margin-left: 8px;
}

/* ---------------- 登录 ---------------- */

.center-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-card {
  width: 100%;
  max-width: 400px;
}

.title {
  margin: 0;
  font-size: 20px;
  text-align: center;
}

.sub {
  margin: 8px 0 22px;
  text-align: center;
  color: #8a94a6;
  font-size: 13px;
}

.block-btn {
  width: 100%;
  margin-top: 6px;
}

.tip {
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: #9aa3b5;
}

/* ---------------- 统计 ---------------- */

.topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid #e8ecf4;
}

.topbar-inner {
  max-width: 1080px;
  margin: 0 auto;
  height: 56px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.body {
  max-width: 1080px;
  margin: 0 auto;
  padding: 18px;
}

.stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.stat {
  flex: 1 1 160px;
  background: #fff;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 2px 10px rgba(31, 39, 51, 0.04);
}

.num {
  display: block;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;

  i {
    font-style: normal;
    font-size: 14px;
    color: #8a94a6;
    margin-left: 2px;
  }
}

.label {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: #8a94a6;
}

.panel {
  margin-bottom: 16px;
  border-radius: 10px;
}

.bar-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 0;
}

.bar-name {
  flex: none;
  width: 240px;
  font-size: 13px;
  color: #37415a;
}

.bar-track {
  flex: 1;
}

.bar-val {
  flex: none;
  width: 40px;
  text-align: right;
  font-size: 13px;
}

h4 {
  margin: 16px 0 8px;
  color: #55607a;
}

.comment-item {
  margin: 6px 0;
  padding: 8px 12px;
  background: #fafbfe;
  border-radius: 8px;
  color: #37415a;
}

@media (max-width: 700px) {
  .bar-name {
    width: 130px;
    font-size: 12px;
  }
}
</style>
